import { PropsWithChildren, Children } from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";

import { media, UNSAFE_media } from "@atlaskit/primitives/responsive";

import { RowRevised, RowRevisedProps } from "./grid-revised";

interface childrenTypes {
    1?: string | undefined;
    2?: string | undefined;
    3?: string | undefined;
    4?: string | undefined;
    5?: string | undefined;
    6?: string | undefined;
    7?: string | undefined;
    8?: string | undefined;
    9?: string | undefined;
    10?: string | undefined;
    11?: string | undefined;
    12?: string | undefined;
    optional?: string | undefined;
}

interface childGridsTypes {
    0?: childrenTypes;
    1?: childrenTypes;
    2?: childrenTypes;
    3?: childrenTypes;
    4?: childrenTypes;
}

const gridOrganizers = (
    breakpoint: number,
    gridCols: Array<string>,
    gridRows?: Array<string>,
    customChildGrids?: childGridsTypes
) => {
    const customChildGridsBreakpoint = customChildGrids
        ? customChildGrids[breakpoint as keyof childGridsTypes]
        : false;

    return `
  grid-template-columns: ${gridCols[breakpoint]};
  ${
      gridRows && gridRows[breakpoint]
          ? `grid-template-rows: ${gridRows[breakpoint]};`
          : ``
  }
  ${
      customChildGrids && customChildGridsBreakpoint
          ? Object.keys(customChildGridsBreakpoint)
                .map(
                    (child) => `
          > *:nth-of-type(${child}) {
            ${
                customChildGridsBreakpoint[
                    child as keyof typeof customChildGridsBreakpoint
                ]
            };
          } 
        `
                )
                .join("")
          : ``
  }`;
};

const gridStyles = (
    gridCols: Array<string>,
    gridRows?: Array<string>,
    customChildGrids?: childGridsTypes,
    isReduced?: boolean
) => css`
    display: grid;
    justify-content: space-between;
    margin: auto;
    max-width: 100%;
    box-sizing: border-box;

    ${UNSAFE_media.below.md} {
        gap: 24px;
        padding-left: 32px;
        padding-right: 32px;

        & + .row-layout {
            margin-top: 24px;
        }
    }

    ${media.above.md} {
        gap: 32px;
        padding-left: 60px;
        padding-right: 60px;

        & + .row-layout {
            margin-top: 32px;
        }
    }

    ${UNSAFE_media.below.xs} {
        width: 328px;
        ${gridOrganizers(0, gridCols, gridRows, customChildGrids)}
    }

    ${UNSAFE_media.only.xs} {
        width: 508px;
        ${gridOrganizers(1, gridCols, gridRows, customChildGrids)}
    }

    ${UNSAFE_media.only.sm} {
        width: ${isReduced ? `588` : `768`}px;
        ${gridOrganizers(2, gridCols, gridRows, customChildGrids)}
    }

    ${UNSAFE_media.only.md} {
        width: ${isReduced ? `768` : `1024`}px;
        ${gridOrganizers(3, gridCols, gridRows, customChildGrids)}
    }

    ${media.above.lg} {
        width: ${isReduced ? `888` : `1168`}px;
        ${gridOrganizers(4, gridCols, gridRows, customChildGrids)}
    }
`;

// DefaultStyles depends on the order of keys in LayoutStyles.
// Any future layouts should be added to the end, not the beginning.
const LayoutStyles = {
    "12": { style: gridStyles(Array(5).fill("minmax(0, 1fr)")), count: 1 },
    "6-6": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0, 1fr)",
            "minmax(0, 1fr) minmax(0, 1fr)",
            "minmax(0, 1fr) minmax(0, 1fr)",
            "minmax(0, 1fr) minmax(0, 1fr)"
        ]),
        count: 2
    },
    "4-4-4": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0, 1fr)",
            "repeat(3, minmax(0, 1fr))",
            "repeat(3, minmax(0, 1fr))",
            "repeat(3, minmax(0, 1fr))"
        ]),
        count: 3
    },
    "3-3-3-3": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0, 1fr) minmax(0, 1fr)",
            "minmax(0, 1fr) minmax(0, 1fr)",
            "repeat(4, minmax(0, 1fr))",
            "repeat(4, minmax(0, 1fr))"
        ]),
        count: 4
    },
    "2-2-2-2-2-2": {
        style: gridStyles([
            "minmax(0, 1fr) minmax(0, 1fr)",
            "minmax(0, 1fr) minmax(0, 1fr)",
            "minmax(0, 1fr) minmax(0, 1fr)",
            "repeat(6, minmax(0, 1fr))",
            "repeat(6, minmax(0, 1fr))"
        ]),
        count: 6
    },
    "4-8": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0, 1fr)",
            "minmax(0px, 0.965fr) minmax(0px, 2.035fr)",
            "minmax(0px, 0.962fr) minmax(0px, 2.038fr)",
            "minmax(0px, 0.97fr) minmax(0px, 2.03fr)"
        ]),
        count: 2
    },
    "7-5": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0, 1fr)",
            "minmax(0px, 1fr) minmax(0px, 1fr)",
            "minmax(0px, 1.4fr) minmax(0px, 1fr)",
            "minmax(0px, 1.4fr) minmax(0px, 1fr)"
        ]),
        count: 2
    },
    "3-9": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0, 1fr)",
            "minmax(0, 1fr)",
            "minmax(0px, .925fr) minmax(0px, 3.075fr)",
            "minmax(0px, .937fr) minmax(0px, 3.063fr)"
        ]),
        count: 2
    },
    "8-4": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0, 1fr)",
            "minmax(0px, 2.035fr) minmax(0px, 0.965fr)",
            "minmax(0px, 2.038fr) minmax(0px, 0.962fr)",
            "minmax(0px, 2.03fr) minmax(0px, 0.97fr)"
        ]),
        count: 2
    },
    "9-3": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0, 1fr)",
            "minmax(0, 1fr)",
            "minmax(0px, 3.075fr) minmax(0px, .925fr)",
            "minmax(0px, 3.063fr) minmax(0px, .937fr)"
        ]),
        count: 2
    },
    "3-3-6": {
        style: gridStyles(
            [
                "minmax(0, 1fr)",
                "minmax(0, 1fr) minmax(0, 1fr)",
                "minmax(0, 1fr) minmax(0, 1fr)",
                "minmax(0px, 0.962fr) minmax(0px, 0.962fr) minmax(0px, 2.076fr)",
                "minmax(0px, 0.967fr) minmax(0px, 0.967fr) minmax(0px, 2.066fr)"
            ],
            [
                "auto",
                "minmax(0, 1fr) minmax(0, auto)",
                "minmax(0, 1fr) minmax(0, auto)",
                "auto",
                "auto"
            ],
            [
                {},
                {
                    3: `grid-area: 2 / 1 / 3 / 3;`
                },
                {
                    3: `grid-area: 2 / 1 / 3 / 3;`
                },
                {},
                {}
            ]
        ),
        count: 3
    },
    "6-3-3": {
        style: gridStyles(
            [
                "minmax(0, 1fr)",
                "minmax(0, 1fr) minmax(0, 1fr)",
                "minmax(0, 1fr) minmax(0, 1fr)",
                "minmax(0px, 2.076fr) minmax(0px, 0.962fr) minmax(0px, 0.962fr)",
                "minmax(0px, 2.066fr) minmax(0px, 0.967fr) minmax(0px, 0.967fr)"
            ],
            [
                "auto",
                "minmax(0, auto) minmax(0, 1fr)",
                "minmax(0, auto) minmax(0, 1fr)",
                "auto",
                "auto"
            ],
            [
                {},
                {
                    1: `grid-area: 1 / 1 / 1 / 3;`,
                    2: `grid-area: 2 / 1 / 3 / 2;`,
                    3: `grid-area: 2 / 2 / 3 / 3;`
                },
                {
                    1: `grid-area: 1 / 1 / 1 / 3;`,
                    2: `grid-area: 2 / 1 / 3 / 2;`,
                    3: `grid-area: 2 / 2 / 3 / 3;`
                },
                {},
                {}
            ]
        ),
        count: 3
    },
    "reduced": {
        style: gridStyles(Array(5).fill("minmax(0, 1fr)"), [], {}, true),
        count: 1
    }
};

type Entries<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];

const getEntries = <T extends object>(obj: T) =>
    Object.entries(obj) as Entries<T>;

const DefaultStyles = Object.fromEntries(
    getEntries(LayoutStyles)
        .slice(0, 5)
        .map(([key, val]) => [val["count"], key])
);

Object.freeze(LayoutStyles);
Object.freeze(DefaultStyles);

export type layout = keyof typeof LayoutStyles;

const verticalAlignment = {
    default: "stretch",
    top: "start",
    bottom: "end",
    center: "center"
};

type AlignTypes = "default" | "top" | "bottom" | "center";

export interface RowProps {
    layout?: layout;
    alignItems?: AlignTypes;
}

const getLayoutStyles = (childCount: number, layout?: layout) => {
    if (layout === undefined) {
        layout = DefaultStyles[childCount];
        if (layout === undefined) {
            throw new Error(`${childCount} is an invalid number of children`);
        }
    }
    if (childCount != LayoutStyles[layout]["count"]) {
        throw new Error(
            `Row with layout "${layout}" should have ${LayoutStyles[layout]["count"]} children`
        );
    }

    return LayoutStyles[layout]["style"];
};

/**
 * @description \<Row\> component is in the process of being deprecated in favor of
 * \<RowRevised\>. All new code should use \<RowRevised\> instead.
 */
export const Row = (props: PropsWithChildren<RowProps>) => {
    const { layout } = props;
    const { alignItems } = props;
    const children = Children.toArray(props.children);
    const style = getLayoutStyles(children.length, layout);

    return (
        <div
            className="row-layout"
            css={css`
                ${style}
                align-items: ${alignItems
                    ? verticalAlignment[alignItems]
                    : verticalAlignment.default};
            `}
        >
            {children.map((child) => child)}
        </div>
    );
};

/**
 * @description RowWrapper accepts props "row" or "rowRevised".
 * Passing "row" prop will render a \<Row\>.
 * Passing "rowRevised" prop will render a \<RowRevised\>.
 * Passing neither prop will render a \<Row\>.
 */
type RowWrapperProps =
    | {
          row?: RowProps;
          rowRevised?: never;
      }
    | {
          row?: never;
          rowRevised?: RowRevisedProps;
      };

/**
 * @description This is a temporary wrapper to allow migrating individual pages which use
 * shared components to the new grid specs It should only be used when migrating existing pages.
 * All net new code should directly use \<RowRevised\>.
 */
export const RowWrapper = (props: PropsWithChildren<RowWrapperProps>) => {
    return props.rowRevised ? (
        <RowRevised {...props.rowRevised}>{props.children}</RowRevised>
    ) : (
        <Row {...props.row}>{props.children}</Row>
    );
};
